import React, { useState, useMemo } from 'react';
import { Users, MapPin, ShieldAlert } from 'lucide-react';
import useGameStore from '../store/gameStore';
import { EMPTY_OBJECT, EMPTY_ARRAY } from '../store/stableEmpties';
import { RECURRENT_CLIENTS, getLoyaltyTier, LOYALTY_TIERS, isClientOnCooldown } from '../data/clientLoyalty';
import ClientLogo from '../components/crm/ClientLogo';
import ClientDetailPanel from '../components/crm/ClientDetailPanel';

/**
 * Clients (CRM) page — `/clients`.
 *
 * 03/06/2026 — Refonte Phase 2 : layout 2 colonnes (sidebar liste +
 * panneau détail), timeline de fidélité, plan de relance, KPIs.
 *
 * Sidebar : 15 clients triés par score décroissant, chaque entrée
 * affiche le logo de marque, le tier de fidélité, et une mini-barre de
 * score. Le clic ouvre le détail dans le panneau de droite.
 */
export default function Clients() {
  const clientLoyalty = useGameStore(s => s.clientLoyalty || EMPTY_OBJECT);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const activeContracts = useGameStore(s => s.activeContracts || EMPTY_ARRAY);
  const [filterSegment, setFilterSegment] = useState('all');
  // Default-select the top client to avoid an empty panel on first load.
  const [selectedId, setSelectedId] = useState(null);

  const rows = useMemo(() => (
    RECURRENT_CLIENTS
      .filter(c => filterSegment === 'all' || c.segment === filterSegment)
      .map(c => ({
        client: c,
        data: clientLoyalty[c.id] || { score: 30, events: [], coolingUntilDay: 0 },
        tier: getLoyaltyTier((clientLoyalty[c.id] || {}).score),
      }))
      .sort((a, b) => (b.data.score || 0) - (a.data.score || 0))
  ), [clientLoyalty, filterSegment]);

  // Auto-select first row when filter changes (or first render).
  const effectiveSelectedId = selectedId && rows.some(r => r.client.id === selectedId)
    ? selectedId
    : (rows[0]?.client.id || null);

  const selectedRow = rows.find(r => r.client.id === effectiveSelectedId);

  const tierCounts = LOYALTY_TIERS.reduce((acc, t) => {
    acc[t.id] = rows.filter(r => r.tier.id === t.id).length;
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 space-y-5" data-testid="clients-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-black text-foreground flex items-center gap-2">
            <Users className="w-6 h-6 text-accent" />
            Clients & relation
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Suivi de la loyauté de vos 15 clients récurrents. Sélectionnez un client pour explorer son plan de relance et son historique.
          </p>
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-ink-700/40 border border-ink-600">
          {[
            { id: 'all', label: 'Tous' },
            { id: 'GC',  label: 'Grands comptes' },
            { id: 'PME', label: 'PME' },
            { id: 'PUB', label: 'Public' },
          ].map(s => (
            <button
              key={s.id}
              onClick={() => setFilterSegment(s.id)}
              className={`px-3 py-1 rounded text-xs font-bold transition-colors ${filterSegment === s.id ? 'bg-accent text-white' : 'text-slate-300 hover:text-foreground'}`}
              data-testid={`segment-filter-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </header>

      {/* Tier breakdown */}
      <div className="grid grid-cols-5 gap-2" data-testid="tier-summary">
        {LOYALTY_TIERS.map(t => (
          <div
            key={t.id}
            className="px-3 py-2 rounded-lg border bg-ink-800/60"
            style={{ borderColor: `${t.color}55` }}
            data-testid={`tier-summary-${t.id}`}
          >
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: t.color }}>{t.label}</div>
            <div className="text-xl font-mono font-bold text-foreground mt-0.5">{tierCounts[t.id] || 0}</div>
          </div>
        ))}
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar list */}
        <aside className="col-span-12 lg:col-span-4 space-y-1.5" data-testid="clients-list">
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500 italic px-3 py-6 text-center">Aucun client dans ce segment.</p>
          ) : rows.map(({ client, data, tier }) => {
            const onCooldown = isClientOnCooldown(clientLoyalty, client.id, totalDays);
            const isSelected = effectiveSelectedId === client.id;
            return (
              <button
                key={client.id}
                type="button"
                onClick={() => setSelectedId(client.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
                  isSelected
                    ? 'bg-ink-700/70 border-accent shadow-lg'
                    : 'bg-ink-800 border-ink-600 hover:border-ink-500 hover:bg-ink-700/40'
                }`}
                data-testid={`client-row-${client.id}`}
              >
                <ClientLogo client={client} size="md" className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className={`text-sm font-bold truncate ${isSelected ? 'text-foreground' : 'text-slate-200'}`}>{client.name}</h3>
                    {onCooldown && (
                      <ShieldAlert className="w-3 h-3 text-rose-400 flex-shrink-0" data-testid={`client-cooldown-${client.id}`} />
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-2.5 h-2.5" />
                    {client.city}
                    <span className="text-slate-600">·</span>
                    <span className="font-mono uppercase tracking-wider">{client.segment}</span>
                  </p>
                  {/* Mini score bar */}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="h-1 flex-1 bg-ink-700 rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${Math.min(100, data.score || 0)}%`, background: tier.color }}
                      />
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 tabular tabular-nums w-7 text-right">{Math.round(data.score || 0)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Detail panel */}
        <main className="col-span-12 lg:col-span-8">
          {selectedRow ? (
            <ClientDetailPanel
              client={selectedRow.client}
              data={selectedRow.data}
              tier={selectedRow.tier}
              totalDays={totalDays}
              activeContracts={activeContracts}
              onCooldown={isClientOnCooldown(clientLoyalty, selectedRow.client.id, totalDays)}
              cooldownLeft={Math.max(0, (selectedRow.data.coolingUntilDay || 0) - totalDays)}
            />
          ) : (
            <div className="bg-ink-800 rounded-lg border border-ink-600 p-12 text-center">
              <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Sélectionnez un client à gauche pour voir son détail.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
